import React, { useCallback, useMemo, useState } from 'react';
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  Tag,
  Link,
  Button,
  Tooltip,
} from '@carbon/react';
import { DirectionMerge } from '@carbon/react/icons';
import { ConfidenceBreakdown } from 'components/confidence-breakdown';
import { EntityFlagTag } from 'components/entity-flag-tag';
import { OperatorTag } from 'components/operator-tag';
import {
  ENTITY_TYPE_LABEL,
  type OperationTag,
  type ReconcileCandidate,
} from './types';
import { EntityClassification, AliasesList } from './alias-fields';
import { ParticipationLinks } from './participation-links';
import { WarningCircleIcon } from './warning-circle-icon';
import type { AliasClusterInfo } from './use-alias-pill-states';
import ReconcileEntityDocuments from './reconcile-entity-documents';
import { entityIdLabel } from 'utils/entity-id';
import styles from './reconcile-candidate-table.module.scss';

interface ReconcileCandidateTableProps {
  candidates: ReconcileCandidate[];
  selectedIds: Set<number>;
  onToggle: (id: number, selected: boolean) => void;
  aliasStates: Map<number, AliasClusterInfo>;
  operationTags: Map<number, OperationTag[]>;
  // Laptop / compact density (mock v25 "laptop mode"): folds the Score % and the
  // Source label + operation tags into a single column, dropping the standalone
  // Source column — the 6-data-column layout for narrower screens. Default
  // (full-monitor) keeps Score and Source as separate columns.
  compact?: boolean;
  // The left-pane rows to highlight (§3.4): the entities of every distinct name
  // selected in Entity Consolidation (a shift/ctrl multi-select lights them all),
  // or the single source entity of the document row selected in a survivor's View
  // Documents table. One highlight owner at a time — resolved by useHighlight.
  highlightedIds?: ReadonlySet<number>;
  // Header select-all (latest mock): when provided, the select column header
  // renders a TableSelectAll driven by these; omitted = plain header.
  onToggleAll?: () => void;
  allSelected?: boolean;
  someSelected?: boolean;
  // Entities locked from selection because a canonical survivor of the opposite
  // type is established (Phase 2 / #27): their checkbox is disabled with a
  // tooltip, rather than refusing a click after the fact.
  lockedIds?: Set<number>;
  lockedReason?: string;
  // Imported entities that can be converted to a canonical entity in place
  // ("Add as New", §3.9). Renders an action on those rows.
  addAsNewIds?: Set<number>;
  onAddAsNew?: (entityId: number) => void;
  // Fired when an address-confidence flag is cleared from a row's Documents
  // drilldown (#26), so the host can drop that entity's flag state.
  onFlagCleared?: (entityId: number) => void;
  // View-document toggle (mock v25): opens a document in the shared inline
  // Document viewer pane instead of a new tab.
  onViewDocument?: (documentId: number, documentName?: string) => void;
  viewedDocumentId?: number | null;
  showOperatorTag?: boolean;
}

type SortKey = 'confidence' | 'source' | 'name';
type SortDir = 'ASC' | 'DESC' | 'NONE';

const confidencePct = (confidence: number | null) => {
  return confidence === null ? null : Math.round(confidence * 100);
};

const highlightClassFor = (criticality?: 'high' | 'medium' | null) => {
  return criticality === 'high'
    ? styles.flagHighlightHigh
    : styles.flagHighlightMed;
};

const ReviewTag: React.FC<{ criticality?: 'high' | 'medium' | null }> = ({
  criticality,
}) => (
  <Tag type={criticality === 'high' ? 'red' : 'warm-gray'} size="sm">
    Review: {criticality === 'high' ? 'High' : 'Med'}
  </Tag>
);

// The address block for a flagged candidate: one address per line (no "; "
// separator), with the highlight + "Review" badge inline next to EACH flagged
// address — an entity can have several flagged addresses. Falls back to the
// whole joined display when per-address data isn't available (e.g. a User-Added
// row).
const FlaggedAddressList: React.FC<{
  addresses?: ReconcileCandidate['addresses'];
  joined: string | null;
  criticality?: 'high' | 'medium' | null;
}> = ({ addresses, joined, criticality }) => {
  if (addresses && addresses.length > 0) {
    return (
      <span className={styles.addressList}>
        {addresses.map((a) => {
          if (!a.flagged) {
            return (
              <span key={a.id} className={styles.addressItem}>
                {a.label}
              </span>
            );
          }
          return (
            <span
              key={a.id}
              className={styles.addressItem}
              data-testid="reconcile-flagged-address"
            >
              <span className={highlightClassFor(a.criticality)}>
                {a.label}
              </span>
              <ReviewTag criticality={a.criticality} />
            </span>
          );
        })}
      </span>
    );
  }

  return (
    <span className={styles.flaggedAddress}>
      <span
        data-testid="reconcile-flagged-address"
        className={highlightClassFor(criticality)}
      >
        {joined ?? '—'}
      </span>
      <ReviewTag criticality={criticality} />
    </span>
  );
};

interface CandidateRowProps {
  c: ReconcileCandidate;
  isSelected: boolean;
  isHighlighted: boolean;
  isLocked: boolean;
  lockedReason: string;
  isExpanded: boolean;
  tags: OperationTag[];
  compact: boolean;
  aliasInfo: AliasClusterInfo | undefined;
  showAddAsNew: boolean;
  onToggle: (id: number, selected: boolean) => void;
  onAddAsNew?: (entityId: number) => void;
  onToggleExpanded: (id: number) => void;
  onFlagCleared?: (entityId: number) => void;
  onViewDocument?: (documentId: number, documentName?: string) => void;
  viewedDocumentId?: number | null;
  showOperatorTag: boolean;
}

// Memoized single candidate row (R21): with a stable candidate object and stable
// callbacks, a row only re-renders when its own derived props (selection,
// highlight, lock, expansion, tags) actually change — so an edit that touches one
// row (or an unrelated part of the model) doesn't re-render the whole list.
const CandidateRowInner: React.FC<CandidateRowProps> = ({
  c,
  isSelected,
  isHighlighted,
  isLocked,
  lockedReason,
  isExpanded,
  tags,
  compact,
  aliasInfo,
  showAddAsNew,
  onToggle,
  onAddAsNew,
  onToggleExpanded,
  onFlagCleared,
  onViewDocument,
  viewedDocumentId,
  showOperatorTag,
}) => {
  const pct = confidencePct(c.confidence);
  const scoreLabel = pct === null ? '—' : `${pct}%`;
  // The Source cell's content (label + flag chip + operation tags). Rendered in
  // its own column in full-monitor mode, or folded under the Score % in compact.
  const sourceContent = (
    <div className={styles.source}>
      <span>{c.sourceLabel}</span>
      {!c.flagged && (
        <EntityFlagTag
          flagCategory={c.flagCategory}
          flagRationale={c.flagRationale}
          flagSeverity={c.flagSeverity}
        />
      )}
      {tags.length > 0 && (
        <div className={styles.opTags}>
          {tags.map((tag) => (
            <Tag
              key={tag.label}
              size="sm"
              type={tag.type}
              className={tag.outline ? styles.outlineTag : undefined}
            >
              {tag.label}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
  // The whole row is a selection target (PM feedback: "no corn hole") — clicks
  // on interactive elements (links, the checkbox itself, graph/expand buttons)
  // keep their own behavior. Attached per-cell because TableExpandRow doesn't
  // accept row-level events.
  const rowClick = (e: React.MouseEvent) => {
    if (c.flagged || isLocked) return;
    const target = e.target as HTMLElement;
    if (target.closest('a, button, input, label')) return;
    onToggle(c.id, !isSelected);
  };
  return (
    <React.Fragment>
      <TableExpandRow
        isExpanded={isExpanded}
        onExpand={() => onToggleExpanded(c.id)}
        aria-label={`Documents for ${c.name}`}
        // Explicit highlight signal (separate from the checkbox's own selection),
        // so it reads accessibly and tests assert real state, not a CSS class.
        data-highlighted={isHighlighted ? 'true' : undefined}
        className={
          [
            styles.selectableRow,
            c.flagged ? styles.flaggedRow : '',
            !c.flagged && c.flagSeverity ? styles.consolidationFlaggedRow : '',
            isHighlighted ? styles.highlightedRow : '',
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
      >
        {/* Dedicated flag column — the yellow address-confidence marker, left of
            the checkbox (mock default-deduplication/v25). An unresolved
            address-confidence flag (`addressFlagged`, #26) keeps the row
            selectable; a hard `flagged` block disables the checkbox instead. */}
        <TableCell className={styles.flagCell} onClick={rowClick}>
          {(c.addressFlagged || c.flagged) && (
            <Tooltip
              align="bottom"
              autoAlign
              label={`"${c.name}" has an unresolved address-confidence issue. It can still be selected, but the flag must be cleared before reconciling — resolve it on Match Review.`}
            >
              <button
                type="button"
                className={styles.flagTrigger}
                aria-label="Address confidence issue"
              >
                <WarningCircleIcon className={styles.flagIcon} />
              </button>
            </Tooltip>
          )}
        </TableCell>
        {/* bx--table-column-checkbox (the app's Carbon prefix is bx) matches
            TableSelectAll's header cell width/padding so the column aligns
            (PM feedback). */}
        <TableCell className="bx--table-column-checkbox" onClick={rowClick}>
          <Checkbox
            id={`reconcile-row-checkbox-${c.id}`}
            labelText=""
            checked={isSelected}
            disabled={isLocked || c.flagged}
            title={isLocked ? lockedReason : undefined}
            onChange={(_e: unknown, { checked }: { checked: boolean }) =>
              onToggle(c.id, checked)
            }
          />
        </TableCell>
        {compact ? (
          // Laptop mode: Score % + Source label/tags share one column.
          <TableCell onClick={rowClick}>
            <div className={styles.scoreSource}>
              <span className={styles.scoreValue}>{scoreLabel}</span>
              {sourceContent}
            </div>
          </TableCell>
        ) : (
          <>
            <TableCell onClick={rowClick}>{scoreLabel}</TableCell>
            <TableCell onClick={rowClick}>{sourceContent}</TableCell>
          </>
        )}
        {/* Add as New (§3.9) — its own column, an icon action (matches the mock's
            canonize/merge affordance). */}
        <TableCell onClick={rowClick} className={styles.addAsNewCell}>
          {showAddAsNew && onAddAsNew && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={DirectionMerge}
              iconDescription="Add as new"
              tooltipPosition="left"
              onClick={() => onAddAsNew(c.id)}
            />
          )}
        </TableCell>
        <TableCell onClick={rowClick}>
          <div className={styles.entity}>
            <span className={styles.entityName}>
              <Link href={`/entities/${c.id}`}>{c.name}</Link>
              {showOperatorTag && c.operator && (
                <span className={styles.nameTag}>
                  <OperatorTag />
                </span>
              )}
            </span>
            <span className={styles.entityMeta}>
              Entity Type: <strong>{ENTITY_TYPE_LABEL[c.type]}</strong>
            </span>
            <span className={styles.entityMeta}>{entityIdLabel(c.id)}</span>
            <span className={styles.entityMeta}>
              Entity Classification:{' '}
              <EntityClassification info={aliasInfo} entityId={c.id} />
            </span>
            <span className={styles.entityMeta}>
              Address:{' '}
              {c.addressFlagged ? (
                <FlaggedAddressList
                  addresses={c.addresses}
                  joined={c.address}
                  criticality={c.addressFlagCriticality}
                />
              ) : (
                (c.address ?? '—')
              )}
            </span>
            {/* Decision #31 — the entity's own workspace/interpretation
                participation as "Title Workspaces: N | Interpretations: M"
                links (workspaces open a popover of runsheet links,
                interpretations the profile tab). Renders nothing when both are
                zero. Sourced from Entity::ParticipationCounts. */}
            <ParticipationLinks
              entityId={c.id}
              entityName={c.name}
              titleWorkspaces={c.titleWorkspaces}
              interpretationCount={c.interpretationCount}
            />
          </div>
        </TableCell>
        <TableCell onClick={rowClick}>
          <AliasesList
            aliases={aliasInfo?.aliases ?? []}
            entityId={c.id}
            linkNames
          />
        </TableCell>
      </TableExpandRow>
      {isExpanded && (
        <TableExpandedRow colSpan={compact ? 7 : 8}>
          <div className={styles.drilldown}>
            <div className={styles.docs}>
              <h6 className={styles.drilldownHeading}>Documents</h6>
              <ReconcileEntityDocuments
                entityId={c.id}
                entityName={c.name}
                address={c.address}
                addresses={c.addresses}
                onFlagCleared={onFlagCleared}
                onViewDocument={onViewDocument}
                viewedDocumentId={viewedDocumentId}
              />
            </div>
            {c.confidenceFactors && c.confidenceFactors.length > 0 && (
              <div className={styles.breakdown}>
                <h6 className={styles.drilldownHeading}>
                  Confidence breakdown
                </h6>
                <ConfidenceBreakdown factors={c.confidenceFactors} />
              </div>
            )}
          </div>
        </TableExpandedRow>
      )}
    </React.Fragment>
  );
};

const CandidateRow = React.memo(
  CandidateRowInner,
  (prev, next) =>
    prev.c === next.c &&
    prev.isSelected === next.isSelected &&
    prev.isHighlighted === next.isHighlighted &&
    prev.isLocked === next.isLocked &&
    prev.lockedReason === next.lockedReason &&
    prev.isExpanded === next.isExpanded &&
    prev.compact === next.compact &&
    prev.showAddAsNew === next.showAddAsNew &&
    prev.showOperatorTag === next.showOperatorTag &&
    prev.aliasInfo === next.aliasInfo &&
    prev.onToggle === next.onToggle &&
    prev.onAddAsNew === next.onAddAsNew &&
    prev.onToggleExpanded === next.onToggleExpanded &&
    prev.onFlagCleared === next.onFlagCleared &&
    prev.onViewDocument === next.onViewDocument &&
    prev.viewedDocumentId === next.viewedDocumentId &&
    prev.tags.length === next.tags.length &&
    prev.tags.every(
      (t, i) => t.label === next.tags[i].label && t.type === next.tags[i].type
    )
);

const NEXT_DIR: Record<SortDir, SortDir> = {
  ASC: 'DESC',
  DESC: 'NONE',
  NONE: 'ASC',
};

// Stable empty tags array so a row with no operation tags keeps a constant prop
// identity across renders (otherwise a fresh [] would defeat row memoization).
const EMPTY_TAGS: OperationTag[] = [];

// v3 suggestions table — the default candidate view. Consolidated columns
// (Confidence · Source + operation tags · Entity · Aliases), sortable, with a
// row-expansion drilldown into documents + the confidence breakdown. The staged
// entity is pinned first; flagged entities can't be selected.
const ReconcileCandidateTable: React.FC<ReconcileCandidateTableProps> = ({
  candidates,
  selectedIds,
  onToggle,
  aliasStates,
  operationTags,
  compact = false,
  highlightedIds,
  onToggleAll,
  allSelected = false,
  someSelected = false,
  lockedIds,
  lockedReason = 'Locked — incompatible with the current survivor’s entity type.',
  addAsNewIds,
  onAddAsNew,
  onFlagCleared,
  onViewDocument,
  viewedDocumentId,
  showOperatorTag = false,
}) => {
  // Default sort: confidence, highest first (v3).
  const [sortKey, setSortKey] = useState<SortKey>('confidence');
  const [sortDir, setSortDir] = useState<SortDir>('DESC');
  // Multiple rows can be expanded at once (mock v2/v3 `advExpandedRowIds`) so
  // the examiner can compare drilldowns side by side.
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  // Stable identity so a memoized row isn't re-rendered by this callback changing.
  const toggleExpanded = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const rows = useMemo(() => {
    const sortValue = (c: ReconcileCandidate): number | string => {
      if (sortKey === 'confidence') return c.confidence ?? -1;
      if (sortKey === 'source') return c.sourceLabel;
      return c.name;
    };

    // Staged (the viewed/anchor entity) is always pinned first, immune to sort
    // (v3). A User-Added entity (chosen from Add Entities, §3.9) pins right below
    // it — also immune to sort — so a just-added entity surfaces at the top of
    // the list rather than sinking to the bottom on its null confidence.
    const isUserAdded = (c: ReconcileCandidate) =>
      c.sourceLabel?.startsWith('User Added') ?? false;
    const staged = candidates.filter((c) => c.isStaged);
    const userAdded = candidates.filter((c) => !c.isStaged && isUserAdded(c));
    const rest = candidates.filter((c) => !c.isStaged && !isUserAdded(c));
    if (sortDir !== 'NONE') {
      const dir = sortDir === 'ASC' ? 1 : -1;
      rest.sort((a, b) => {
        const av = sortValue(a);
        const bv = sortValue(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }
    return [...staged, ...userAdded, ...rest];
  }, [candidates, sortKey, sortDir]);

  const headerSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir(key === 'confidence' ? 'DESC' : 'ASC');
      return;
    }
    setSortDir((prev) => NEXT_DIR[prev]);
  };

  const sortProps = (key: SortKey) => ({
    isSortable: true,
    sortDirection: sortKey === key ? sortDir : 'NONE',
    isSortHeader: sortKey === key,
    onClick: () => headerSort(key),
  });

  return (
    <TableContainer>
      <Table
        className={[styles.table, compact ? styles.compact : '']
          .filter(Boolean)
          .join(' ')}
      >
        <TableHead>
          <TableRow>
            <TableExpandHeader
              aria-label="Expand all rows"
              enableToggle
              isExpanded={
                rows.length > 0 && rows.every((c) => expandedIds.has(c.id))
              }
              onExpand={() => {
                const allOpen =
                  rows.length > 0 && rows.every((c) => expandedIds.has(c.id));
                setExpandedIds(
                  allOpen ? new Set() : new Set(rows.map((c) => c.id))
                );
              }}
            />
            {/* Dedicated flag column — empty header; only a flagged row's own
                cell below ever fills it (mock default-deduplication/v25). */}
            <TableHeader
              className={styles.flagHeader}
              aria-label="Address flag"
            />
            {onToggleAll ? (
              <TableSelectAll
                id="reconcile-select-all"
                name="reconcile-select-all"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                ariaLabel="Select all rows"
                onSelect={onToggleAll}
              />
            ) : (
              <TableHeader>
                <span className={styles.srOnly}>Select</span>
              </TableHeader>
            )}
            <TableHeader {...sortProps('confidence')}>Score</TableHeader>
            {!compact && (
              <TableHeader {...sortProps('source')}>Source</TableHeader>
            )}
            <TableHeader
              className={styles.addAsNewCol}
              aria-label="Add as new"
            />
            <TableHeader {...sortProps('name')}>Entity</TableHeader>
            <TableHeader>Aliases</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((c) => (
            <CandidateRow
              key={c.id}
              c={c}
              isSelected={selectedIds.has(c.id)}
              isHighlighted={highlightedIds?.has(c.id) ?? false}
              isLocked={lockedIds?.has(c.id) ?? false}
              lockedReason={lockedReason}
              isExpanded={expandedIds.has(c.id)}
              tags={operationTags.get(c.id) ?? EMPTY_TAGS}
              compact={compact}
              aliasInfo={aliasStates.get(c.id)}
              showAddAsNew={addAsNewIds?.has(c.id) ?? false}
              onToggle={onToggle}
              onAddAsNew={onAddAsNew}
              onToggleExpanded={toggleExpanded}
              onFlagCleared={onFlagCleared}
              onViewDocument={onViewDocument}
              viewedDocumentId={viewedDocumentId}
              showOperatorTag={showOperatorTag}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReconcileCandidateTable;
